import { useBlobUpload, usePackageId } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { useState } from 'react';

import { Field, SheetForm } from '@/components';
import { FileUpload } from '@/components/file-upload';
import { errorToast, successToast } from '@/components/toast';

import { UploadDocumentFormData } from './types';
import { validateUploadDocumentForm } from './validator';

export const UploadDocumentForm = () => {
  const packageId = usePackageId();
  const { upload: uploadBlob } = useBlobUpload();

  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = async (): Promise<boolean> => {
    // Prevent duplicate submissions
    if (isUploading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return false;
    }

    // Validate form data
    const formData: UploadDocumentFormData = { files };
    const validation = validateUploadDocumentForm(formData);

    if (!validation.isValid) {
      setError(validation.errors.files || 'Please select at least one file to upload');
      return false;
    }

    setIsUploading(true);
    setError(undefined);

    try {
      // Upload files one by one using the blob upload hook
      for (const file of files) {
        await uploadBlob(file, packageId);
      }
      successToast({ message: 'Documents uploaded successfully' });
      setFiles([]);
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload documents';
      setError(errorMessage);
      errorToast({ message: errorMessage });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFiles([]);
    setError(undefined);
  };

  return (
    <SheetForm
      title="Upload Documents"
      description="Select documents to upload to this package"
      trigger={<Button>Upload Document</Button>}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSubmitting={isUploading}
      submitLabel={isUploading ? 'Uploading...' : 'Upload'}
    >
      <Field label="Documents" htmlFor="documents" required error={error}>
        <FileUpload
          disabled={isUploading}
          files={files}
          setFiles={setFiles}
          maxFiles={10}
          maxSize={50 * 1024 * 1024} // 50MB
          accept={{
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc', '.docx'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.ms-excel': ['.xls', '.xlsx'],
            'image/*': ['.jpg', '.jpeg', '.png'],
          }}
        />
      </Field>
    </SheetForm>
  );
};
